/**
 * Shared formulas for the SDMS sheet on OPEN (speed, owner request 2026-10-01).
 *
 * A re-merged sheet carries no cached results, so Univer calculates every formula on open — and PARSES each one.
 * VOC has 8,458 distinct formula texts, more than Univer's formula AST cache (5,000), so it kept re-parsing them
 * (PFAS: ~3.5 s of a ~10 s first calculation). Most of those texts are one formula per column that differs only by
 * its row: `=IF(B8="","",J8/M8*100)`, `=IF(B9="","",J9/M9*100)`, … Excel stores such a run as a shared formula; so
 * does Univer (`si`): the first cell keeps `f` + `si`, the others carry the `si` alone and Univer derives their formula
 * by moving the first one's relative references down — one parse instead of hundreds.
 *
 * A run is shared ONLY when every cell's formula equals the first one's with its RELATIVE row numbers moved by the
 * row distance — checked on the text, so a formula that is not exactly that keeps its own `f`:
 *   – text inside "string literals" and 'quoted sheet names' is never read as a reference;
 *   – an absolute row ($8) must be the same number in every cell;
 *   – a function name with digits (LOG10, ATAN2) is not a reference;
 *   – a formula with a row-only range (5:12) is never shared — its offset rules are not worth the risk.
 * Same column only, so no column reference moves. Values (`v`), types and styles are left exactly as they are.
 *
 * Applied once, to the snapshot the workbook is CREATED from — never to the snapshots the in-place sheet refresh
 * writes into an open workbook (their `si` ids would be a second conversion's).
 */

type CellLike = { f?: unknown; si?: unknown };
type SheetLike = { cellData?: Record<string | number, Record<string | number, CellLike | null | undefined> | null | undefined> };
type SnapshotLike = { sheets?: Record<string, SheetLike> };

const REF_AT = /(\$?)([A-Za-z]{1,3})(\$?)(\d+)/y;
const ROW_RANGE = /(^|[^A-Za-z0-9_.$])\$?\d+\s*:\s*\$?\d+(?![\d.A-Za-z(])/;

/**
 * The formula with each RELATIVE row number written as its distance from `row0` (0-based) — `{-0}`, `{+1}`, … —
 * so two formulas that differ only by their row read the same. Null when the formula must not be shared.
 */
export function relativeRowKey(formula: string, row0: number): string | null {
  const row1 = row0 + 1;
  let out = '';
  let plain = '';   // the formula outside strings / quoted names, for the row-range check
  for (let i = 0; i < formula.length;) {
    const ch = formula[i]!;
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      for (; j < formula.length; j++) {
        if (formula[j] === ch) { if (formula[j + 1] === ch) { j++; continue; } break; }
      }
      out += formula.slice(i, j + 1);
      plain += ' ';
      i = j + 1;
      continue;
    }
    const prev = i > 0 ? formula[i - 1]! : '';
    if (/[A-Za-z$]/.test(ch) && !/[A-Za-z0-9_.]/.test(prev)) {
      REF_AT.lastIndex = i;
      const m = REF_AT.exec(formula);
      const next = m ? formula[i + m[0].length] ?? '' : '';
      if (m && !/[A-Za-z0-9_.(!]/.test(next)) {
        const [whole, colAbs, col, rowAbs, rowDigits] = m as unknown as [string, string, string, string, string];
        out += rowAbs ? whole : `${colAbs}${col}{${Number(rowDigits) - row1}}`;
        plain += whole;
        i += whole.length;
        continue;
      }
    }
    out += ch;
    plain += ch;
    i++;
  }
  return ROW_RANGE.test(plain) ? null : out;
}

/**
 * Turn each column's runs of row-shifted copies of one formula into a Univer shared formula, in place. Returns how
 * many cells now carry only an `si`. `idPrefix` keeps the ids apart from any other conversion.
 */
export function shareFormulas(snapshot: SnapshotLike, idPrefix = `sf${Math.random().toString(36).slice(2, 8)}`): number {
  let shared = 0;
  let next = 0;
  for (const sheet of Object.values(snapshot.sheets ?? {})) {
    const cd = sheet?.cellData;
    if (!cd) continue;
    // column → key → the cells (row order) that carry it
    const byCol = new Map<number, Map<string, Array<{ r: number; cell: CellLike }>>>();
    const rows = Object.keys(cd).map(Number).sort((a, b) => a - b);
    for (const r of rows) {
      const row = cd[r];
      if (!row) continue;
      for (const [cKey, cell] of Object.entries(row)) {
        if (!cell || typeof cell.f !== 'string' || !cell.f || cell.si !== undefined) continue;
        const key = relativeRowKey(cell.f, r);
        if (key === null) continue;
        const c = Number(cKey);
        let keys = byCol.get(c);
        if (!keys) { keys = new Map(); byCol.set(c, keys); }
        let run = keys.get(key);
        if (!run) { run = []; keys.set(key, run); }
        run.push({ r, cell });
      }
    }
    for (const keys of byCol.values())
      for (const run of keys.values()) {
        if (run.length < 2) continue;
        const si = `${idPrefix}-${next++}`;
        run[0]!.cell.si = si;                       // the origin keeps its formula
        for (let i = 1; i < run.length; i++) {
          const cell = run[i]!.cell;
          delete cell.f;
          cell.si = si;
          shared++;
        }
      }
  }
  return shared;
}
